import { AxiosInstance } from "axios";
import { CreatePartOrderItemWithoutPartOrderInput } from "../generated/nest-graphql";

/**
 * RECEIPT-SCAN
 *
 * Photographs a parts receipt and asks NuAI to read the lines off it, so a technician
 * confirms an order instead of typing it.
 *
 * OFF here. The endpoint has never been called from this app and no token has been
 * verified against it, so enabling it is a deploy decision. With the flag off the
 * dialog behaves exactly as it did before: pick a photo, type the order, save.
 *
 * `grep -rn RECEIPT-SCAN` finds every line of this feature.
 */
export const RECEIPT_SCAN_ENABLED: boolean = true;

/**
 * Returns the fixture below instead of calling the network, so the whole flow can be
 * exercised on a laptop with no AI backend. Needs `RECEIPT_SCAN_ENABLED` as well —
 * nothing calls the extractor until the feature is on.
 */
export const USE_RECEIPT_FIXTURE: boolean = false;

/** The only caller anywhere else is ops-frontend's JobPartOrders. Same endpoint, same field. */
export const RECEIPT_SCAN_PATH = "/parts/generate";

/** The shared client allows 30s, which is short for OCR on a phone photo. */
export const RECEIPT_SCAN_TIMEOUT_MS = 60000;

/**
 * Every other client in this repo sends `techWebApp`, but the working call to NuAI —
 * ops-frontend's — sends `opsAdmin`, and nothing here can see what the service does
 * with it. Left as the value that is known to work; flip it once there is a token to
 * test with.
 */
export const RECEIPT_SCAN_SENT_FROM = "opsAdmin";

export type ExtractedLine = {
  /** Local row identity. Survives editing, unlike an index. */
  key: string;
  name: string;
  partNumber: string;
  units: number | null;
  vendorUnitCost: number | null;
};

export type ExtractedReceipt = {
  orderNumber: string | null;
  storeName: string | null;
  notes: string | null;
  orderTotalCost: number | null;
  lines: ExtractedLine[];
};

let lineKeySeed = 0;
const nextLineKey = () => `receipt-line-${(lineKeySeed += 1)}`;

export const createEmptyLine = (): ExtractedLine => ({
  key: nextLineKey(),
  name: "",
  partNumber: "",
  units: null,
  vendorUnitCost: null,
});

const firstValue = (source: Record<string, any>, keys: string[]) => {
  const key = keys.find((candidate) => source[candidate] !== undefined && source[candidate] !== null);
  return key ? source[key] : null;
};

const toText = (value: any): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  return "";
};

/** Receipt amounts arrive as `"$1,234.50"` as often as they arrive as numbers. */
const toNumber = (value: any): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;
  const cleaned = value.replace(/[^0-9.-]/g, "");
  if (!cleaned || cleaned === "-" || cleaned === ".") return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const toStoreName = (value: any): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value.trim() || null;
  const name = toText(firstValue(value, ["name", "storeName", "vendor"]));
  return name || null;
};

const pickLines = (source: Record<string, any>): any[] => {
  const candidate = firstValue(source, ["items", "parts", "lineItems", "line_items", "partItems"]);
  return Array.isArray(candidate) ? candidate : [];
};

/**
 * The response shape is unverified — ops spreads it straight into a part order and
 * never validates it — so read every field by alias and treat anything unreadable as
 * absent. A wrong guess must cost the technician a blank field, not a crash.
 */
export const normaliseReceipt = (raw: any): ExtractedReceipt => {
  const source = (raw?.partOrder ?? raw?.data ?? raw ?? {}) as Record<string, any>;

  const lines = pickLines(source)
    .map((entry: any) => {
      const line = (entry ?? {}) as Record<string, any>;
      return {
        key: nextLineKey(),
        name: toText(firstValue(line, ["name", "description", "partName", "part_name", "item"])),
        partNumber: toText(firstValue(line, ["partNumber", "part_number", "sku", "number"])),
        units: toNumber(firstValue(line, ["units", "quantity", "qty", "count"])),
        vendorUnitCost: toNumber(
          firstValue(line, [
            "vendorUnitCost",
            "vendor_unit_cost",
            "unitCost",
            "unit_cost",
            "unitPrice",
            "price",
            "cost",
          ])
        ),
      };
    })
    // A row with neither a name nor a part number is noise off the receipt footer.
    .filter((line) => line.name || line.partNumber);

  return {
    orderNumber: toText(firstValue(source, ["orderNumber", "order_number", "invoiceNumber", "invoice_number"])) || null,
    storeName: toStoreName(firstValue(source, ["store", "storeName", "vendor"])),
    notes: toText(firstValue(source, ["notes", "note", "comments"])) || null,
    orderTotalCost: toNumber(firstValue(source, ["orderTotalCost", "order_total_cost", "total", "totalCost"])),
    lines,
  };
};

/** Shaped like a NuAI response, deliberately mixed-case to exercise the adapter. */
export const RECEIPT_FIXTURE = {
  orderNumber: "WO-448213",
  store: { name: "OReilly 4412", vendor: "O'Reilly" },
  order_total_cost: "$312.84",
  items: [
    { name: "Ceramic brake pads — front", part_number: "BP-9921", quantity: "1", unit_cost: "$68.40" },
    { name: "Brake rotor — front", part_number: "RT-2210", quantity: 2, unit_cost: 79.99 },
    { description: "Brake fluid DOT 3", sku: "BF-0032", qty: "1", price: "$9.46" },
    { name: "", part_number: "", qty: "", price: "$312.84" },
  ],
};

const fixtureDelay = () => new Promise((resolve) => setTimeout(resolve, 900));

export const extractPartsFromReceipt = async (client: AxiosInstance, file: File): Promise<ExtractedReceipt> => {
  if (USE_RECEIPT_FIXTURE) {
    await fixtureDelay();
    return normaliseReceipt(RECEIPT_FIXTURE);
  }

  const formData = new FormData();
  formData.append("file", file);

  const response = await client.post(RECEIPT_SCAN_PATH, formData, {
    headers: { "Content-Type": "multipart/form-data", "req-sent-from": RECEIPT_SCAN_SENT_FROM },
    timeout: RECEIPT_SCAN_TIMEOUT_MS,
  });

  return normaliseReceipt(response.data);
};

/**
 * Status is deliberately omitted: the backend defaults a new item to `Ordered`, and a
 * technician logging an order has not collected anything yet.
 */
export const toItemsInput = (lines: ExtractedLine[]): CreatePartOrderItemWithoutPartOrderInput[] =>
  lines
    .filter((line) => line.name.trim() || line.partNumber.trim())
    .map((line) => ({
      name: line.name.trim() || null,
      partNumber: line.partNumber.trim() || null,
      units: line.units,
      vendorUnitCost: line.vendorUnitCost,
    }));
